import React from 'react';
import { formatCurrency, getDefaultCurrency, getChangeColorClass } from '../../utils/formatters';
import Tooltip from '../common/Tooltip';
import CategoryBreakdownTable from './CategoryBreakdownTable';

const CategoryBreakdown = ({ title, categories, getRandomColor }) => {
  // Check if any categories have non-zero amounts, or change columns exist
  const values = Object.values(categories);
  const hasChangeCols = values.some(cat => typeof cat.currentPercentage === 'number' && typeof cat.afterPercentage === 'number');
  const hasAllocations = values.some(cat => (cat.amount || 0) > 0) || hasChangeCols;

  if (!hasAllocations) {
    return null;
  }

  const currency = getDefaultCurrency();
  // completed amounts are shown inline under Amount; keep for potential future use
  // const hasCompletedCol = values.some(cat => (cat.completedAmount || 0) > 0);
  // Total for percentage tooltips (sum of totals for all categories)
  const totalForPct = values.reduce((sum, cat) => sum + (cat.amount || 0) + (cat.completedAmount || 0), 0);

  return (
    <CategoryBreakdownTable title={title} currency={currency} hasChangeCols={hasChangeCols}>
            {values
              .sort((a, b) => b.amount - a.amount)
              .map((category, index) => (
                <tr key={category.id} className="border-b dark:border-gray-700 text-sm">
                  <td className="py-1.5 dark:text-white">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2" 
                        style={{ 
                          backgroundColor: category.color || getRandomColor(index + (title === 'Location' ? 5 : 0))
                        }}
                      ></div>
                      {category.name}
                    </div>
                  </td>
                  <td className="py-1.5 dark:text-white">
                    {(() => {
                      const completed = category.completedAmount || 0;
                      const total = hasChangeCols ? (category.amount || 0) + completed : (category.amount || 0);
                      const shareOfTotal = totalForPct > 0 ? (total / totalForPct) * 100 : 0;
                      const completedPctOfGroup = total > 0 ? (completed / total) * 100 : 0;
                      if (completed <= 0) {
                        return (
                          <Tooltip title={`${shareOfTotal.toFixed(1)}% of total allocation`} position="top" arrow={true} animation="shift" theme="dark" size="small" distance={8}>
                            <span className="border-b border-dotted border-current cursor-help">{formatCurrency(total, currency)}</span>
                          </Tooltip>
                        );
                      }
                      if (total > 0 && completed >= total) {
                        return (
                          <Tooltip title={`${shareOfTotal.toFixed(1)}% of total allocation`} position="top" arrow={true} animation="shift" theme="dark" size="small" distance={8}>
                            <span className="text-gray-400 dark:text-gray-500 line-through border-b border-dotted border-current cursor-help">
                              {formatCurrency(total, currency)}
                            </span>
                          </Tooltip>
                        );
                      }
                      return (
                        <span className="text-xs">
                          <Tooltip title={`${completedPctOfGroup.toFixed(1)}% complete`} position="top" arrow={true} animation="shift" theme="dark" size="small" distance={8}>
                            <span className="border-b border-dotted border-current cursor-help">{formatCurrency(completed, currency)}</span>
                          </Tooltip>
                          <span className="mx-1 text-gray-400">/</span>
                          <Tooltip title={`${shareOfTotal.toFixed(1)}% of total allocation`} position="top" arrow={true} animation="shift" theme="dark" size="small" distance={8}>
                            <span className="border-b border-dotted border-current cursor-help">{formatCurrency(total, currency)}</span>
                          </Tooltip>
                        </span>
                      );
                    })()}
                  </td>
                  {!hasChangeCols && (
                    <td className="py-1.5 dark:text-white">{(category.percentage || 0).toFixed(1)}%</td>
                  )}
                  {hasChangeCols && (() => {
                    const currentPct = category.currentPercentage || 0;
                    const afterPct = category.afterPercentage || 0;
                    const delta = Number((afterPct - currentPct).toFixed(1));
                    if (delta === 0) {
                      return (
                        <td className="py-1.5 dark:text-white">
                          <span className="whitespace-nowrap">{currentPct.toFixed(1)}%</span>
                        </td>
                      );
                    }
                    const cls = getChangeColorClass(delta);
                    const label = `${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`;
                    const tip = `${currentPct.toFixed(1)}% -> ${afterPct.toFixed(1)}%`;
                    return (
                      <td className="py-1.5 dark:text-white">
                        <Tooltip title={tip} position="top" arrow={true} animation="shift" theme="dark" size="small" distance={8}>
                          <span className={`${cls} font-medium border-b border-dotted border-current cursor-help whitespace-nowrap`}>{label}</span>
                        </Tooltip>
                      </td>
                    );
                  })()}
                </tr>
              ))}
    </CategoryBreakdownTable>
  );
};

export default CategoryBreakdown; 